import type { ButtonProps as BaseButtonProps } from "@base-ui/react/button";
import { Button as BaseButton } from "@base-ui/react/button";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import clsx from "clsx";
import type React from "react";
import {
  type ButtonHTMLAttributes,
  type CSSProperties,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { vars } from "../../theme/theme.css";
import { generateColorVariants } from "../../utils/functions";
import { Spinner } from "../Spinner";
import {
  type ButtonColor,
  buttonBaseClass,
  buttonBorderRadiusVariants,
  buttonColorVariants,
  buttonPaddingVariants,
  buttonSizeVariants,
  colorActiveVar,
  colorHoverVar,
  colorTextVar,
  colorVar,
  iconOnlyClass,
  linkSizeVariants,
  linkVariantClass,
  outlineVariantClass,
  primaryVariantClass,
  segmentedVariantClass,
  textVariantClass,
  tonalVariantClass,
} from "./styles.css";

export const BUTTON_OPTIONS = {
  default: "default",
  outlined: "outlined",
  tonal: "tonal",
  text: "text",
  segmented: "segmented",
  link: "link",
} as const;

const variantMap: Record<string, string> = {
  default: primaryVariantClass,
  outlined: outlineVariantClass,
  tonal: tonalVariantClass,
  text: textVariantClass,
  link: linkVariantClass,
  segmented: segmentedVariantClass,
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "tonal" | "outlined" | "text" | "segmented" | "link";
  size?: "small" | "medium" | "large";
  icon?: React.ReactNode;
  color?: ButtonColor;
  customColor?: string;
  isLoading?: boolean;
  disabled?: boolean;
  isSelected?: boolean;
  triggerKey?: string;
  /** Base UI render prop — renders a custom element instead of <button>. */
  render?: BaseButtonProps["render"];
}

export const Button = forwardRef(function Button(
  {
    variant = "default",
    size = "large",
    icon,
    customColor,
    color,
    isLoading,
    isSelected,
    children,
    triggerKey,
    className: consumerClassName,
    ...props
  }: ButtonProps,
  ref: React.Ref<HTMLButtonElement>,
) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const mergedRef = useCallback(
    (node: HTMLButtonElement | null) => {
      (buttonRef as { current: HTMLButtonElement | null }).current = node;
      if (typeof ref === "function") ref(node);
      else if (ref)
        (ref as { current: HTMLButtonElement | null }).current = node;
    },
    [ref],
  );

  const customColorStyles = useMemo(() => {
    if (!customColor) return undefined;
    const variants = generateColorVariants(customColor);
    return assignInlineVars({
      [colorVar]: variants.default,
      [colorHoverVar]: variants.dark,
      [colorActiveVar]: variants.darker,
      [colorTextVar]: variants.darker,
    }) as unknown as CSSProperties;
  }, [customColor]);

  const textAndIcon = !!icon && !!children;

  // Dev-mode warning for icon-only buttons without aria-label
  if (
    process.env.NODE_ENV === "development" &&
    icon &&
    !children &&
    !props["aria-label"]
  ) {
    console.warn(
      "Button: icon-only button should have an aria-label prop for accessibility",
    );
  }

  // Compose className from variant + size + padding + border-radius
  const variantClass = variantMap[variant] ?? primaryVariantClass;
  const colorClass =
    !customColor && color ? buttonColorVariants[color] : undefined;

  const isLink = variant === "link";
  const isIconOnly = !!icon && !children;
  const paddingType = textAndIcon ? "textAndIcon" : icon ? "icon" : "text";
  const paddingClass =
    isLink || isIconOnly ? undefined : buttonPaddingVariants[size][paddingType];
  const radiusClass = buttonBorderRadiusVariants[size];
  const sizeClass = isLink ? linkSizeVariants[size] : buttonSizeVariants[size];

  const className = clsx(
    buttonBaseClass,
    variantClass,
    colorClass,
    sizeClass,
    paddingClass,
    radiusClass,
    isIconOnly && iconOnlyClass,
    consumerClassName,
  );

  useEffect(() => {
    if (!triggerKey) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === triggerKey) {
        if (!triggerKey || event.key !== triggerKey) return;

        const activeElement = document.activeElement;
        const isInputFocused =
          activeElement instanceof HTMLInputElement ||
          activeElement instanceof HTMLTextAreaElement;

        if (isInputFocused) return;

        const button = buttonRef.current;
        if (!button) return;

        const rect = button.getBoundingClientRect();
        const isVisible = rect.width > 0 && rect.height > 0;

        if (!isVisible) return;

        const topElement = document.elementFromPoint(
          rect.left + rect.width / 2,
          rect.top + rect.height / 2,
        );
        const isOnTop = topElement === button || button.contains(topElement);

        if (!isOnTop) return;

        button.click();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [triggerKey]);

  return (
    <BaseButton
      ref={mergedRef}
      className={className}
      style={customColorStyles}
      data-selected={isSelected}
      data-slot="button"
      {...props}
      disabled={isLoading || props.disabled}
      focusableWhenDisabled={false}
      aria-busy={isLoading}
      aria-disabled={isLoading}
      onClick={(e) => {
        if (isLoading) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        props.onClick?.(e);
      }}
    >
      <span
        data-slot="button-content"
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "inherit",
        }}
      >
        <span
          data-slot="button-inner"
          style={{
            visibility: isLoading ? "hidden" : "visible",
            display: "inline-flex",
            alignItems: "center",
            gap: "inherit",
          }}
        >
          {icon && <span data-slot="button-icon">{icon}</span>}
          {children}
        </span>
        {isLoading && (
          <span
            data-slot="button-spinner"
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Spinner size={16} color={vars.colors.neutral.light} />
          </span>
        )}
      </span>
    </BaseButton>
  );
});

Button.displayName = "Button";
